import React, { useState, useEffect } from 'react';
import './CommandManagement.css';

const API_BASE = process.env.REACT_APP_API_BASE;

const CommandManagement = () => {
    const [activeTab, setActiveTab] = useState('create');
    const [commands, setCommands] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        id: '',
        commandName: '',
        embedTitle: '',
        embedDescription: '',
        embedColor: '#5865f2',
        embedImageUrl: '',
        embedThumbnailUrl: '',
        fields: []
    });

    useEffect(() => {
        updatePreview();
    }, [formData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const addField = (title = '', value = '', inline = false) => {
        setFormData(prev => ({
            ...prev,
            fields: [...prev.fields, { id: Date.now(), title, value, inline }]
        }));
    };

    const removeField = (fieldId) => {
        setFormData(prev => ({
            ...prev,
            fields: prev.fields.filter(f => f.id !== fieldId)
        }));
    };

    const resetForm = () => {
        setIsEditMode(false);
        setFormData({
            id: '',
            commandName: '',
            embedTitle: '',
            embedDescription: '',
            embedColor: '#5865f2',
            embedImageUrl: '',
            embedThumbnailUrl: '',
            fields: []
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const token = localStorage.getItem('discord_token');
        
        try {
            const url = isEditMode ? `${API_BASE}/api/commands/${formData.id}` : `${API_BASE}/api/commands`;
            const response = await fetch(url, {
                method: isEditMode ? 'PUT' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    commandName: formData.commandName,
                    embedTitle: formData.embedTitle,
                    embedDescription: formData.embedDescription,
                    embedColor: formData.embedColor,
                    embedImageUrl: formData.embedImageUrl,
                    embedThumbnailUrl: formData.embedThumbnailUrl,
                    fields: formData.fields.map(f => ({
                        name: f.title,
                        value: f.value,
                        inline: f.inline
                    }))
                })
            });

            const result = await response.json();
            
            if (result.success) {
                alert(`Command ".${formData.commandName}" erfolgreich ${isEditMode ? 'aktualisiert' : 'erstellt'}!`);
                resetForm();
                setActiveTab('manage');
                loadCommands();
            } else {
                alert(result.message || 'Fehler bei der Operation');
            }
        } catch (error) {
            alert(`Verbindungsfehler: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const loadCommands = async () => {
        setLoading(true);
        const token = localStorage.getItem('discord_token');
        
        try {
            const response = await fetch(`${API_BASE}/api/commands`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const result = await response.json();
            
            if (result.success) {
                setCommands(result.data || []);
            }
        } catch (error) {
            console.error('Error loading commands:', error);
        } finally {
            setLoading(false);
        }
    };

    const editCommand = (command) => {
        setIsEditMode(true);
        setFormData({
            id: command.id,
            commandName: command.command_name,
            embedTitle: command.embed_title,
            embedDescription: command.embed_description || '',
            embedColor: command.embed_color || '#5865f2',
            embedImageUrl: command.embed_image_url || '',
            embedThumbnailUrl: command.embed_thumbnail_url || '',
            fields: command.embed_fields ? (typeof command.embed_fields === 'string' ? JSON.parse(command.embed_fields) : command.embed_fields).map(f => ({
                id: Date.now() + Math.random(),
                title: f.name || '',
                value: f.value || '',
                inline: f.inline || false
            })) : []
        });
        setActiveTab('create');
    };

    const deleteCommand = async (commandId, commandName) => {
        if (!window.confirm(`Möchtest du den Command ".${commandName}" wirklich unwiderruflich löschen?`)) {
            return;
        }

        setLoading(true);
        const token = localStorage.getItem('discord_token');
        
        try {
            const response = await fetch(`${API_BASE}/api/commands/${commandId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const result = await response.json();
            
            if (result.success) {
                alert(`Command ".${commandName}" erfolgreich gelöscht!`);
                loadCommands();
            } else {
                alert(result.message || 'Fehler beim Löschen');
            }
        } catch (error) {
            alert(`Verbindungsfehler: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const renderPreview = () => {
        const { embedTitle, embedDescription, embedColor, embedImageUrl, embedThumbnailUrl, fields } = formData;

        return (
            <div className="discord-preview-container">
                <div className="discord-message-header">
                    <img src="https://i.imgur.com/avatar.png" className="discord-avatar" alt="Bot Avatar" />
                    <span className="discord-username">salerno City | System</span>
                    <span className="discord-bot-tag">BOT</span>
                    <span className="discord-timestamp">Jetzt</span>
                </div>
                <div className="discord-embed-container" style={{ borderLeftColor: embedColor }}>
                    <div className="embed-content-row">
                        <div className="embed-left">
                            {embedTitle && <div className="embed-title">{embedTitle}</div>}
                            {embedDescription && <div className="embed-description">{embedDescription}</div>}

                            {fields.length > 0 && (
                                <div className="embed-fields">
                                    {fields.map(field => (
                                        <div key={field.id} className={`embed-field ${field.inline ? 'embed-field-inline' : ''}`}>
                                            <div className="embed-field-title">{field.title || 'Feld-Titel'}</div>
                                            <div className="embed-field-value">{field.value || 'Feld-Wert'}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {embedImageUrl && <img src={embedImageUrl} className="embed-image" alt="Embed content" />}
                        </div>

                        {embedThumbnailUrl && (
                            <div className="embed-right">
                                <img src={embedThumbnailUrl} className="embed-thumbnail" alt="Thumbnail" />
                            </div>
                        )}
                    </div>

                    <div className="embed-footer">
                        <img src="https://i.imgur.com/avatar.png" className="embed-footer-icon" alt="Footer Icon" />
                        Salerno City Command Helper
                    </div>
                </div>
            </div>
        );
    };

    const updatePreview = () => {
        // Preview is automatically updated via useEffect and renderPreview
        // This function can be used for any additional preview logic if needed
    };

    return (
        <div className="command-management">
            <div className="page-header">
                <h2><span className="tab-icon">💻</span> Command Management</h2>
                <p>Erstelle und verwalte Discord-Commands für deinen Bot</p>
            </div>

            <div className="tabs-container">
                <div className="tabs">
                    <button 
                        className={`tab ${activeTab === 'create' ? 'active' : ''}`}
                        onClick={() => setActiveTab('create')}
                    >
                        <span className="tab-icon">➕</span> Command erstellen
                    </button>
                    <button 
                        className={`tab ${activeTab === 'manage' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('manage'); loadCommands(); }}
                    >
                        <span className="tab-icon">📋</span> Commands verwalten
                    </button>
                </div>

                {activeTab === 'create' && (
                    <div className="tab-content active">
                        <div className="form-section-preview">
                            <div className="form-section">
                                <h3>
                                    <span className="tab-icon">⚙️</span> 
                                    {isEditMode ? `Command ".${formData.commandName}" bearbeiten` : 'Neuen Command konfigurieren'}
                                </h3>
                                <form onSubmit={handleSubmit}>
                                    <div className="form-row">
                                        <div className="form-group">
                                            <label className="form-label">
                                                <span className="tab-icon">💻</span> Command Name (ohne Punkt)
                                            </label>
                                            <input
                                                type="text"
                                                name="commandName"
                                                className="form-control"
                                                placeholder="z.B. unban"
                                                value={formData.commandName}
                                                onChange={handleInputChange}
                                                required
                                                disabled={isEditMode}
                                                style={{ width:'60%' }}

                                            />
                                            <small className="text-muted">Der Command wird als .{formData.commandName || 'name'} verfügbar sein</small>
                                        </div>
                                        
                                        <div className="form-group">
                                            <label className="form-label">
                                                <span className="tab-icon">🎨</span> Embed Farbe
                                            </label>
                                            <div className="color-input-group">
                                                <input
                                                    type="color"
                                                    name="embedColor"
                                                    className="form-control"
                                                    value={formData.embedColor}
                                                    onChange={handleInputChange}
                                                    style={{ width: '60px', height: '46px' }}
                                                />
                                                <div 
                                                    className="color-preview" 
                                                    style={{ backgroundColor: formData.embedColor }}
                                                    onClick={() => document.querySelector('input[name="embedColor"]').click()}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            <span className="tab-icon">📝</span> Embed Titel
                                        </label>
                                        <input
                                            type="text"
                                            name="embedTitle"
                                            className="form-control"
                                            placeholder="z.B. Benutzer wurde entbannt"
                                            value={formData.embedTitle}
                                            onChange={handleInputChange}
                                            style={{ width: '50%' }}

                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            <span className="tab-icon">📄</span> Embed Beschreibung
                                        </label>
                                        <textarea
                                            name="embedDescription"
                                            className="form-control"
                                            placeholder="Detaillierte Beschreibung des Embeds..."
                                            value={formData.embedDescription}
                                            onChange={handleInputChange}
                                            rows={4}
                                        ></textarea>
                                    </div>

                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <label className="form-label mb-0">
                                                <span className="tab-icon">📋</span> Embed Felder (optional)
                                            </label>
                                            <button type="button" className="btn btn-secondary btn-sm" onClick={() => addField()}>
                                                <span className="tab-icon">➕</span> Feld hinzufügen
                                            </button>
                                        </div>
                                        {formData.fields.map(field => (
                                            <div key={field.id} className="field-card">
                                                <button type="button" className="btn-close delete-field-btn" onClick={() => removeField(field.id)}></button>
                                                <div className="form-group">
                                                    <label className="form-label">Titel/Überschrift</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={field.title}
                                                        placeholder="Feld-Titel"
                                                        style={{ width: '35%' }}

                                                        onChange={(e) => {
                                                            const newFields = formData.fields.map(f => 
                                                                f.id === field.id ? { ...f, title: e.target.value } : f
                                                            );
                                                            setFormData(prev => ({ ...prev, fields: newFields }));
                                                        }}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label className="form-label">Value/Inhalt</label>
                                                    <textarea
                                                        className="form-control"
                                                        placeholder="Feld-Wert"
                                                        value={field.value}
                                                        style={{ width: '35%' }}

                                                        onChange={(e) => {
                                                            const newFields = formData.fields.map(f => 
                                                                f.id === field.id ? { ...f, value: e.target.value } : f
                                                            );
                                                            setFormData(prev => ({ ...prev, fields: newFields }));
                                                        }}
                                                    ></textarea>
                                                </div>
                                                <div className="form-check">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        id={`${field.id}-inline`}
                                                        checked={field.inline}
                                                        onChange={(e) => {
                                                            const newFields = formData.fields.map(f => 
                                                                f.id === field.id ? { ...f, inline: e.target.checked } : f
                                                            );
                                                            setFormData(prev => ({ ...prev, fields: newFields }));
                                                        }}
                                                    />
                                                    <label className="form-check-label" htmlFor={`${field.id}-inline`}>Inline anzeigen</label>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            <span className="tab-icon">🖼️</span> Embed Thumbnail URL (optional)
                                        </label>
                                        <input
                                            type="url"
                                            name="embedThumbnailUrl"
                                            className="form-control"
                                            placeholder="https://example.com/thumbnail.png"
                                            value={formData.embedThumbnailUrl}
                                            onChange={handleInputChange}
                                            style={{ width: '50%' }}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            <span className="tab-icon">🖼️</span> Embed Bild URL (optional)
                                        </label>
                                        <input
                                            type="url"
                                            name="embedImageUrl"
                                            className="form-control"
                                            placeholder="https://example.com/image.png"
                                            value={formData.embedImageUrl}
                                            onChange={handleInputChange}
                                            style={{ width: '50%' }}
                                        />
                                    </div>

                                    <div id="form-actions">
                                        <button type="submit" className="btn btn-primary" disabled={loading}>
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2"></span>
                                                    {isEditMode ? 'Aktualisiere...' : 'Erstelle Command...'}
                                                </>
                                            ) : (
                                                <>
                                                    <span className="tab-icon">{isEditMode ? '🔄' : '➕'}</span> 
                                                    {isEditMode ? 'Command aktualisieren' : 'Command erstellen'}
                                                </>
                                            )}
                                        </button>
                                        <button type="button" className="btn btn-warning" onClick={resetForm}>
                                            <span className="tab-icon">🔄</span> Zurücksetzen
                                        </button>
                                    </div>
                                </form>
                            </div>

                            <div className="form-section">
                                <h3><span className="tab-icon">👁️</span> Live-Vorschau</h3>
                                {renderPreview()}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'manage' && (
                    <div className="tab-content active">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h3><span className="tab-icon">📋</span> Alle Commands</h3>
                            <button className="btn btn-primary" onClick={loadCommands} disabled={loading}>
                                <span className="tab-icon">🔄</span> Aktualisieren
                            </button>
                        </div>
                        
                        {loading ? (
                            <div className="loading-state">
                                <div className="spinner-border text-primary"></div>
                                <span>Lade Commands...</span>
                            </div>
                        ) : commands.length === 0 ? (
                            <div className="empty-state">
                                <span className="tab-icon" style={{ fontSize: '4em', opacity: 0.3 }}>💻</span>
                                <h4>Keine Commands gefunden</h4>
                                <p>Erstelle deinen ersten Command im "Command erstellen" Tab</p>
                            </div>
                        ) : (
                            <div className="commands-grid">
                                {commands.map(command => (
                                    <div key={command.id} className="command-card fade-in">
                                        <div className="command-name">
                                            <span className="tab-icon">💻</span>
                                            .{command.command_name}
                                        </div>
                                        <div className="command-title">{command.embed_title}</div>
                                        <div className="command-description">
                                            {command.embed_description || 'Keine Beschreibung'}
                                        </div>
                                        <div className="command-meta">
                                            <span className="tab-icon">📅</span> Erstellt: {new Date(command.created_at).toLocaleDateString('de-DE')}
                                        </div>
                                        <div className="command-actions">
                                            <button className="btn btn-secondary" onClick={() => editCommand(command)}>
                                                <span className="tab-icon">✏️</span> Bearbeiten
                                            </button>
                                            <button className="btn btn-danger" onClick={() => deleteCommand(command.id, command.command_name)} disabled={loading}>
                                                <span className="tab-icon">🗑️</span> Löschen
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CommandManagement;
